// Berichte: billed (merged), raw sum and overlap for every group, as a chart or a table, with CSV, a printable report
// (PDF from the browser) and an invoice summary.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Interval } from '../../../packages/core/src/index.ts';
import { dayRange, isoDate, monthRange, parseLocal, quarterRange, weekRange, yearRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { reportCsv, reportCsvName } from '../model/exports.ts';
import {
  buildReport, groupingLabel, groupingOverlaps, periodLabel, REPORT_GROUPINGS, REPORT_PERIODS, TEAM_GROUPINGS, type ReportGrouping, type ReportModel, type ReportPeriod, type ReportRow,
} from '../model/report.ts';
import { isOwner } from '../model/data.ts';
import { totalTokens } from '../model/session.ts';
import { Banner, download, EmptyState, Field, Loading, Menu, MenuDivider, MenuItem, ScreenHeader, Select, TextInput } from '../components/common.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import type { Store } from '../store/store.ts';
import { Button, SegmentedControl, SourceBadge, StatTile } from '../ui/ds.tsx';
import { BarChart, Legend, type BarGroup } from '../ui/chart.tsx';
import { InvoiceList } from './Invoices.tsx';

export function periodRange(period: ReportPeriod, now: number, timeZone: string, custom: Interval | null): Interval {
  switch (period) {
    case 'thisWeek': return weekRange(now, timeZone);
    case 'lastWeek': return weekRange(now, timeZone, -1);
    case 'thisMonth': return monthRange(now, timeZone);
    case 'lastMonth': return monthRange(now, timeZone, -1);
    case 'thisQuarter': return quarterRange(now, timeZone);
    case 'thisYear': return yearRange(now, timeZone);
    case 'custom': return custom && custom.end > custom.start ? custom : monthRange(now, timeZone);
  }
}

export function rowColor(row: ReportRow | undefined, index: number): string {
  if (!row) return 'var(--ink-3)';
  if (row.slot !== null) return `var(--project-${row.slot})`;
  if (row.source) return row.source === 'human' ? 'var(--human)' : 'var(--agent)';
  if (row.key === 'billable') return 'var(--ink)';
  if (row.key === 'internal') return 'var(--ink-3)';
  return `var(--project-${(index % 8) + 1})`;
}

export function rangeLabel(store: Store, range: Interval): string {
  return `${store.fmt.dayMonthYear(range.start)} – ${store.fmt.dayMonthYear(range.end - 1)}`;
}

export function Reports() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(60_000);
  const { reportPeriod: period, reportCustom: custom, reportTable: asTable } = store.ui;
  // Owners in a team see the team's time (and can group by member) or their own.
  const teamReports = store.ws.team.size > 1 && isOwner(store.ws);
  const team = teamReports && store.ui.reportTeam;
  const grouping: ReportGrouping = !team && store.ui.reportGrouping === 'member' ? 'project' : store.ui.reportGrouping;
  const range = useMemo(() => periodRange(period, now, store.timeZone, custom), [period, custom, Math.floor(now / 3_600_000), store.timeZone]);
  const loaded = useRange(range, true, team);
  const report = loaded.data ? buildReport(t, store.ws, loaded.data, range, grouping, now, store.timeZone, true) : undefined;
  const hasUsage = !!report?.usage && totalTokens(report.usage) > 0;
  const setCustom = (start: number | null, end: number | null) => {
    const base = custom ?? { start: dayRange(now, store.timeZone, -30).start, end: dayRange(now, store.timeZone).end };
    const next = { start: start ?? base.start, end: end ?? base.end };
    if (next.end > next.start) store.setUi({ reportCustom: next });
  };
  return (
    <div className="screen">
      <ScreenHeader title={t('Berichte')} subtitle={rangeLabel(store, range)}>
        {teamReports ? (
          <SegmentedControl size="sm" label={t('Umfang')} value={team ? 'team' : 'me'} onChange={(v) => store.setUi({ reportTeam: v === 'team' })}
            options={[{ value: 'me', label: t('Ich') }, { value: 'team', label: t('Team') }]} />
        ) : null}
        <Select value={period} aria-label={t('Zeitraum')} onChange={(e) => store.setUi({ reportPeriod: e.target.value as ReportPeriod })} className="select--auto">
          {REPORT_PERIODS.map((p) => <option key={p} value={p}>{periodLabel(t, p)}</option>)}
        </Select>
        <Menu label={t('Exportieren')} align="end" trigger={(props) => <Button icon="share" disabled={!report || report.total.rawMs === 0} {...props}>{t('Exportieren')}</Button>}>
          {(menu) => report ? (
            <>
              {store.pro ? <>
                <MenuItem icon="doc" onSelect={() => { menu.close(); store.openSheet({ kind: 'reportPrint', range }); }}>{t('Bericht als PDF …')}</MenuItem>
                <MenuItem icon="euro" onSelect={() => { menu.close(); store.openSheet({ kind: 'invoice', range }); }}>{t('Rechnungszusammenfassung …')}</MenuItem>
              </> : <MenuItem icon="doc" onSelect={() => { menu.close(); navigate({ name: 'pro' }); }}>{t('PDF und Rechnung mit Pro …')}</MenuItem>}
              <MenuDivider />
              <MenuItem icon="download" onSelect={() => { menu.close(); download(reportCsvName(t, report, store.timeZone), reportCsv(t, report, store.lang)); }}>{t('Tabelle als CSV …')}</MenuItem>
            </>
          ) : null}
        </Menu>
      </ScreenHeader>
      {period === 'custom' ? (
        <div className="form-row custom-range">
          <Field label={t('Von')}>
            <TextInput type="date" value={isoDate(custom?.start ?? dayRange(now, store.timeZone, -30).start, store.timeZone)}
              onChange={(e) => { const v = parseLocal(e.target.value, store.timeZone); if (v !== null) setCustom(v, null); }} />
          </Field>
          <Field label={t('Bis')}>
            <TextInput type="date" value={isoDate((custom?.end ?? dayRange(now, store.timeZone).end) - 1, store.timeZone)}
              onChange={(e) => { const v = parseLocal(e.target.value, store.timeZone); if (v !== null) setCustom(null, dayRange(v, store.timeZone).end); }} />
          </Field>
        </div>
      ) : null}
      {!report ? (loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />) : (
        <>
          <div className="stats">
            <StatTile label={t('Abgerechnet')} value={fmt.decimalHours(report.total.billedMs)} unit="h"
              hint={report.total.rawMs > report.total.billedMs ? t('%@ h Summe · Überschneidung 1× gezählt', fmt.decimalHours(report.total.rawMs)) : t('Keine Überschneidung')} />
            <StatTile label={t('Eigene Zeit')} accent="human" value={fmt.decimalHours(report.total.humanMs)} unit="h" />
            <StatTile label={t('Agent-Zeit')} accent="agent" value={fmt.decimalHours(report.total.agentMs)} unit="h"
              hint={hasUsage && report.usage ? t('%@ Tokens', fmt.tokens(totalTokens(report.usage))) : undefined} />
            <StatTile label={t('Abrechenbar')} value={fmt.number(report.total.amountCents / 100)} unit="€" hint={t('%@ h abrechenbar', fmt.decimalHours(report.total.billableMs))} />
          </div>
          {report.total.rawMs === 0 ? (
            <EmptyState icon="chart" title={t('Keine Zeit in diesem Zeitraum')} message={t('Wähle einen anderen Zeitraum oder erfasse Zeit mit dem Timer. Agent-Zeit kommt von selbst dazu.')} />
          ) : (
            <>
              <ChartCard report={report} grouping={grouping} asTable={asTable} now={now} team={team} />
              <ReportTable report={report} hasUsage={hasUsage} />
            </>
          )}
          {report.unassignedMs > 0 ? (
            <Banner icon="folder" actions={<Button size="sm" variant="plain" onClick={() => { store.setUi({ sessionsFilter: 'month' }); navigate({ name: 'sessions' }); }}>{t('Sessions zeigen')}</Button>}>
              {t('%@ Agent-Zeit ohne Projekt ist nicht enthalten.', fmt.duration(report.unassignedMs))}
            </Banner>
          ) : null}
        </>
      )}
      {isOwner(store.ws) ? <InvoiceList /> : null}
    </div>
  );
}

export function bucketLabel(store: Store, report: ReportModel, bucket: Interval): string {
  switch (report.bucketSize) {
    case 'day': return store.fmt.dayMonthNumeric(bucket.start);
    case 'week': return store.t('KW %lld', store.fmt.week(bucket.start));
    case 'month': return store.fmt.monthShort(bucket.start);
  }
}

export function chartGroups(store: Store, report: ReportModel, now: number): { groups: BarGroup[]; legend: { key: string; label: string; color: string }[] } {
  // Nine or more groups: the smallest fold into "Weitere" (brand book).
  const visible = report.rows.slice(0, 8);
  const colors = new Map(visible.map((r, i) => [r.key, rowColor(r, i)]));
  const order = [...visible.map((r) => r.key), 'other'];
  const groups = report.buckets.filter((b) => b.start <= now).map((bucket): BarGroup => {
    const sums = new Map<string, number>();
    for (const p of report.points.filter((x) => x.bucket.start === bucket.start)) {
      const key = colors.has(p.key) ? p.key : 'other';
      sums.set(key, (sums.get(key) ?? 0) + p.billedMs);
    }
    return {
      key: String(bucket.start), label: bucketLabel(store, report, bucket),
      bars: order.filter((k) => sums.has(k)).map((k) => ({
        key: k, label: k === 'other' ? store.t('Weitere') : report.rows.find((r) => r.key === k)?.label ?? k, value: sums.get(k) ?? 0, color: colors.get(k) ?? 'var(--ink-3)',
      })),
    };
  });
  const legend = [...visible.map((r, i) => ({ key: r.key, label: r.label, color: rowColor(r, i) })),
    ...(report.rows.length > 8 ? [{ key: 'other', label: store.t('Weitere'), color: 'var(--ink-3)' }] : [])];
  return { groups, legend };
}

function ChartCard({ report, grouping, asTable, now, team }: { report: ReportModel; grouping: ReportGrouping; asTable: boolean; now: number; team: boolean }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const { groups, legend } = chartGroups(store, report, now);
  const tableGroups = report.rows.slice(0, 6);
  return (
    <section className="tl-card panel">
      <div className="chart-head">
        <h2>{t('Abgerechnet nach')}</h2>
        <SegmentedControl<ReportGrouping> size="sm" label={t('Gruppierung')} value={grouping} onChange={(v) => store.setUi({ reportGrouping: v })}
          options={(team ? TEAM_GROUPINGS : REPORT_GROUPINGS).map((g) => ({ value: g, label: groupingLabel(t, g) }))} />
        <span className="spacer" />
        <SegmentedControl size="sm" label={t('Darstellung')} value={asTable ? 'table' : 'chart'} onChange={(v) => store.setUi({ reportTable: v === 'table' })}
          options={[{ value: 'chart', label: t('Diagramm') }, { value: 'table', label: t('Tabelle') }]} />
      </div>
      {asTable ? (
        <div className="table__scroll">
          <table className="data-table">
            <thead><tr><th>{t('Zeitraum')}</th>{tableGroups.map((g) => <th key={g.key} className="table__num">{g.label}</th>)}</tr></thead>
            <tbody>
              {report.buckets.filter((b) => b.start <= now).map((bucket) => (
                <tr key={bucket.start}>
                  <td>{bucketLabel(store, report, bucket)}</td>
                  {tableGroups.map((g) => {
                    const ms = report.points.find((p) => p.bucket.start === bucket.start && p.key === g.key)?.billedMs ?? 0;
                    return <td key={g.key} className={ms > 0 ? 'table__num' : 'table__num table__num--faint'}>{ms > 0 ? fmt.duration(ms) : '–'}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <BarChart groups={groups} stacked={!groupingOverlaps(grouping)} format={(ms) => fmt.duration(ms)} label={t('Abgerechnete Stunden je Zeitraum und Gruppe')} />
          <Legend items={legend} />
        </>
      )}
      <p className="note">{groupingOverlaps(grouping)
        ? t('Quellen und Provider überschneiden sich: jede Gruppe ist für sich zusammengeführt, deshalb ist die Summe der Gruppen größer als die abgerechnete Zeit.')
        : t('Pro Projekt zusammengeführt, dann addiert. Überschneidungen zählen einmal.')}</p>
    </section>
  );
}

function ReportTable({ report, hasUsage }: { report: ReportModel; hasUsage: boolean }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const showAmount = !groupingOverlaps(report.grouping);
  const columns = ['minmax(200px, 1fr)', '96px', '88px', '136px', ...(hasUsage ? ['80px'] : []), ...(showAmount ? ['96px'] : [])].join(' ');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreColumns, setMoreColumns] = useState(false);
  useEffect(() => {
    const scroll = scrollRef.current!;
    const update = () => setMoreColumns(scroll.scrollWidth - scroll.clientWidth - scroll.scrollLeft > 1);
    const observer = new ResizeObserver(update);
    observer.observe(scroll);
    observer.observe(scroll.firstElementChild!);
    scroll.addEventListener('scroll', update, { passive: true });
    update();
    return () => { observer.disconnect(); scroll.removeEventListener('scroll', update); };
  }, []);
  return (
    <div className="tl-card table">
      <div ref={scrollRef} className="table__scroll" role="region" aria-label={t('Berichtstabelle')} tabIndex={0}>
        <div className="report-table">
          <div className="table__head" style={{ gridTemplateColumns: columns }}>
            <span>{groupingLabel(t, report.grouping)}</span><span className="table__num">{t('Abgerechnet')}</span><span className="table__num">{t('Summe')}</span>
            <span className="table__num">{t('Überschneidung')}</span>{hasUsage ? <span className="table__num">{t('Tokens')}</span> : null}
            {showAmount ? <span className="table__num">{t('Betrag')}</span> : null}
          </div>
          {report.rows.map((row, i) => {
            const content = (
              <>
                <span className="table__cell">
                  {row.source ? <SourceBadge source={row.source} label={row.label} /> : <><span className="tl-dot" style={{ background: rowColor(row, i) }} /><span className="clip">{row.label}</span></>}
                </span>
                <span className="table__num">{fmt.duration(row.totals.billedMs)}</span>
                <span className="table__num table__num--muted">{fmt.duration(row.totals.rawMs)}</span>
                <span className="table__num table__num--muted">{fmt.duration(Math.max(0, row.totals.rawMs - row.totals.billedMs))}</span>
                {hasUsage ? <span className="table__num table__num--muted">{row.usage ? fmt.tokens(totalTokens(row.usage)) : '–'}</span> : null}
                {showAmount ? <span className="table__num">{fmt.money(row.totals.amountCents)}</span> : null}
              </>
            );
            return report.grouping === 'project'
              ? <button key={row.key} type="button" className="row-button table__row" style={{ gridTemplateColumns: columns }} onClick={() => navigate({ name: 'project', id: row.key })}>{content}</button>
              : <div key={row.key} className="table__row" style={{ gridTemplateColumns: columns }}>{content}</div>;
          })}
          <div className="table__row table__row--total" style={{ gridTemplateColumns: columns }}>
            <span>{t('Gesamt')}</span>
            <span className="table__num">{fmt.duration(report.total.billedMs)}</span>
            <span className="table__num table__num--muted">{fmt.duration(report.total.rawMs)}</span>
            <span className="table__num table__num--muted">{fmt.duration(Math.max(0, report.total.rawMs - report.total.billedMs))}</span>
            {hasUsage ? <span className="table__num table__num--muted">{report.usage ? fmt.tokens(totalTokens(report.usage)) : '–'}</span> : null}
            {showAmount ? <span className="table__num">{fmt.money(report.total.amountCents)}</span> : null}
          </div>
        </div>
      </div>
      {moreColumns ? <p className="note table__more no-print">{t('Weitere Spalten rechts')} →</p> : null}
    </div>
  );
}
